/**
 * Cookie consent (PLAN.md §19).
 *
 * Strictly necessary cookies always run; analytics and marketing tags load only
 * after the visitor agrees. The decision is kept first-party with a version, so
 * a policy or category change re-asks rather than silently reusing an old
 * answer. Stored client-side for now — recording it against the application
 * server-side is a later step (§19).
 */
export const CONSENT_VERSION = 1

const KEY = 'kcm_consent'

export interface Consent {
  version: number
  /** ISO timestamp of the decision. */
  at: string
  analytics: boolean
  marketing: boolean
}

/** The stored decision, or null when none has been made under this version. */
export function readConsent(): Consent | null {
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return null
    const consent = JSON.parse(raw) as Consent
    // A version bump re-asks: the categories or the policy changed.
    return consent.version === CONSENT_VERSION ? consent : null
  } catch {
    return null
  }
}

export function writeConsent(analytics: boolean, marketing: boolean): Consent {
  const consent: Consent = {
    version: CONSENT_VERSION,
    at: new Date().toISOString(),
    analytics,
    marketing,
  }
  try {
    localStorage.setItem(KEY, JSON.stringify(consent))
  } catch {
    // Private mode or disabled storage: the choice still holds for this load.
  }
  return consent
}

/** Whether the visitor has agreed to any non-essential tag loading. */
export function tagsAllowed(consent: Consent | null): boolean {
  return consent !== null && (consent.analytics || consent.marketing)
}

/**
 * Withdraws the stored decision so the banner asks again.
 *
 * The caller reloads afterwards: a tag already injected under the old consent
 * cannot be un-run in place, so a fresh load is the honest way to stop it.
 */
export function clearConsent(): void {
  try {
    localStorage.removeItem(KEY)
  } catch {
    // Nothing to clear when storage is unavailable.
  }
}
